
import { useState } from 'react';

interface Product {
  id: string;
  name: string;
  price: number;
  originalPrice?: number;
  image: string;
  description: string;
  category: string;
  isNew?: boolean;
  isOnSale?: boolean;
}

interface ProductCardProps {
  product: Product;
  onAddToCart: (productId: string, quantity: number, productName: string, productPrice: number, productImage: string) => void;
  onViewDetails: () => void;
}

export default function ProductCard({ product, onAddToCart, onViewDetails }: ProductCardProps) {
  const [quantity, setQuantity] = useState(1);
  const [isHovered, setIsHovered] = useState(false);

  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAddToCart(product.id, quantity, product.name, product.price, product.image);
  };

  const handleViewDetails = () => {
    onViewDetails();
  };

  const handleQuantityChange = (e: React.MouseEvent, change: number) => {
    e.stopPropagation();
    if (change === -1) {
      setQuantity(Math.max(1, quantity - 1));
    } else {
      setQuantity(quantity + 1);
    }
  };

  return (
    <div 
      className="group relative bg-black border border-gray-800 rounded-lg overflow-hidden transition-all duration-700 ease-out hover:border-[#C6A45C] hover:shadow-2xl hover:shadow-[#C6A45C]/20 transform hover:scale-102 cursor-pointer mx-2"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleViewDetails}
      data-product-shop
      style={{ maxWidth: '320px', margin: '0 auto' }}
    >
      {/* Animated Border Effect */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#C6A45C]/30 to-transparent animate-shimmer"></div>
      </div>

      {/* Luxury Glow Effect */}
      <div className="absolute -inset-1 bg-gradient-to-r from-[#C6A45C]/0 via-[#C6A45C]/20 to-[#C6A45C]/0 rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-700 blur-sm pointer-events-none"></div>

      {/* Product Image Container */}
      <div className="relative aspect-square overflow-hidden bg-gray-900">
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-cover transition-all duration-700 ease-out group-hover:scale-105 group-hover:brightness-110"
        />
        
        {/* Luxury Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-700 pointer-events-none"></div>
        
        {/* Floating Particles Effect */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none">
          <div className="absolute top-4 left-4 w-1 h-1 bg-[#C6A45C] rounded-full animate-ping"></div>
          <div className="absolute top-8 right-6 w-1 h-1 bg-[#C6A45C] rounded-full animate-ping" style={{ animationDelay: '0.5s' }}></div>
          <div className="absolute bottom-6 left-6 w-1 h-1 bg-[#C6A45C] rounded-full animate-ping" style={{ animationDelay: '1s' }}></div>
        </div>

        {/* Badges */}
        {product.isNew && (
          <div className="absolute top-3 left-3 bg-gradient-to-r from-[#C6A45C] to-yellow-600 text-black px-3 py-1 text-xs font-thin uppercase tracking-wider rounded-full shadow-lg animate-pulse pointer-events-none" style={{ fontFamily: '"Lato", sans-serif' }}>
            NUEVO
          </div>
        )}
        {product.isOnSale && (
          <div className="absolute top-3 right-3 bg-gradient-to-r from-red-600 to-red-700 text-white px-3 py-1 text-xs font-thin uppercase tracking-wider rounded-full shadow-lg animate-bounce pointer-events-none" style={{ fontFamily: '"Lato", sans-serif' }}>
            OFERTA
          </div>
        )}

        {/* Quick Add Button */}
        <div className={`absolute bottom-4 left-1/2 transform -translate-x-1/2 transition-all duration-500 ${isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div className="bg-black/90 backdrop-blur-sm border border-[#C6A45C]/50 rounded-lg p-3">
            <div className="flex items-center space-x-2">
              <button
                onClick={(e) => handleQuantityChange(e, -1)}
                className="w-7 h-7 bg-[#C6A45C] text-black rounded-full flex items-center justify-center text-base font-thin hover:bg-yellow-500 transition-all duration-300 hover:scale-110 shadow-lg"
                style={{ fontFamily: '"Lato", sans-serif' }}
              >
                -
              </button>
              <span className="text-[#C6A45C] font-thin text-base min-w-[2rem] text-center bg-gray-800/80 px-2 py-1 rounded border border-gray-600" style={{ fontFamily: '"Lato", sans-serif' }}>{quantity}</span>
              <button
                onClick={(e) => handleQuantityChange(e, 1)}
                className="w-7 h-7 bg-[#C6A45C] text-black rounded-full flex items-center justify-center text-base font-thin hover:bg-yellow-500 transition-all duration-300 hover:scale-110 shadow-lg"
                style={{ fontFamily: '"Lato", sans-serif' }}
              >
                +
              </button>
              <button
                onClick={handleAddToCart}
                className="bg-gradient-to-r from-[#C6A45C] to-yellow-600 text-black px-3 py-1 rounded-lg text-xs font-thin hover:from-yellow-500 hover:to-[#C6A45C] transition-all duration-300 transform hover:scale-105 whitespace-nowrap uppercase tracking-wider"
                style={{ fontFamily: '"Lato", sans-serif' }}
              >
                AGREGAR
              </button>
            </div>
          </div>
        </div>

        {/* Click to view details indicator */}
        <div className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 transition-all duration-500 pointer-events-none ${isHovered ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`}>
          <div className="bg-black/80 backdrop-blur-sm border border-[#C6A45C]/50 rounded-full p-3">
            <i className="ri-eye-line text-[#C6A45C] text-xl"></i>
          </div>
        </div>
      </div>

      {/* Product Info */}
      <div className="p-4 bg-black border-t border-gray-800 group-hover:border-[#C6A45C]/50 transition-all duration-700">
        <h3 className="text-gray-200 font-thin text-lg mb-2 group-hover:text-[#C6A45C] transition-colors duration-500 uppercase tracking-wider" style={{ fontFamily: '"Lato", sans-serif' }}>
          {product.name}
        </h3>
        <div className="flex items-center space-x-2">
          <span className="text-[#C6A45C] font-thin text-xl" style={{ fontFamily: '"Lato", sans-serif' }}>
            ${product.price}
          </span>
          {product.originalPrice && (
            <span className="text-gray-400 line-through text-sm font-thin" style={{ fontFamily: '"Lato", sans-serif' }}>
              ${product.originalPrice}
            </span>
          )}
        </div>
        
        {/* Luxury Accent Line */}
        <div className="mt-3 h-px bg-gradient-to-r from-transparent via-[#C6A45C] to-transparent opacity-0 group-hover:opacity-100 transition-all duration-700 transform scale-x-0 group-hover:scale-x-100"></div>
      </div>

      {/* Corner Accent */}
      <div className="absolute top-0 right-0 w-0 h-0 border-l-[20px] border-l-transparent border-t-[20px] border-t-[#C6A45C] opacity-0 group-hover:opacity-30 transition-all duration-700 pointer-events-none"></div>
    </div>
  );
}
